import type { Message } from '../../shared/types/src';
import { ThreadMessage } from '../../shared/types/src';

class LocalDatabaseService {
  private dbName = 'mentra-v2-db';
  private dbVersion = 2;

  private openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(new Error('Error opening IndexedDB.'));
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('therapist_messages')) {
          const store = db.createObjectStore('therapist_messages', { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
        if (!db.objectStoreNames.contains('chat_history')) {
          const chatStore = db.createObjectStore('chat_history', { keyPath: 'id' });
          chatStore.createIndex('by_user_model', ['userId', 'model'], { unique: false });
          chatStore.createIndex('by_user', 'userId', { unique: false });
        }
      };
    });
  }

  // --- Secure Message Handoff Methods ---

  async addMessages(messages: ThreadMessage[]): Promise<void> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('therapist_messages', 'readwrite');
      const store = transaction.objectStore('therapist_messages');

      messages.forEach(msg => store.put(msg));

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(new Error('Error writing messages to IndexedDB.'));
    });
  }

  async getMessages(): Promise<ThreadMessage[]> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('therapist_messages', 'readonly');
      const store = transaction.objectStore('therapist_messages');
      const index = store.index('timestamp');
      const request = index.getAll();

      request.onsuccess = () => resolve(request.result.reverse()); // Show most recent first
      request.onerror = () => reject(new Error('Error reading messages from IndexedDB.'));
    });
  }

  // --- Chat History Methods (IndexedDB) ---

  async addChatMessage(userId: string, model: 'claude' | 'openai', message: Message): Promise<void> {
    const db = await this.openDb();
    const messageWithUser = { ...message, userId, model };
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('chat_history', 'readwrite');
      const store = transaction.objectStore('chat_history');
      const request = store.put(messageWithUser);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Error adding message to IndexedDB.'));
    });
  }

  async getChatHistory(userId: string): Promise<{ claude: Message[], openai: Message[] }> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('chat_history', 'readonly');
      const store = transaction.objectStore('chat_history');
      const index = store.index('by_user');
      const request = index.getAll(IDBKeyRange.only(userId));

      request.onsuccess = () => {
        const allMessages: (Message & { model?: 'claude' | 'openai' })[] = request.result;
        
        const claude = allMessages
          .filter(m => m.model === 'claude' || m.sender === 'user')
          .sort((a,b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          
        const openai = allMessages
          .filter(m => m.model === 'openai' || m.sender === 'user')
          .sort((a,b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        // By filtering for the model type on AI messages but including all user messages
        // in both, we allow the frontend to create a merged, de-duplicated view.
        resolve({ claude, openai });
      };
      request.onerror = () => reject(new Error('Error fetching chat history from IndexedDB.'));
    });
  }

  async clearChatHistory(userId: string): Promise<void> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('chat_history', 'readwrite');
      const store = transaction.objectStore('chat_history');
      const index = store.index('by_user');
      const request = index.openCursor(IDBKeyRange.only(userId));

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve(); // All matching records deleted
        }
      };
      request.onerror = () => reject(new Error('Error clearing chat history.'));
    });
  }

  // --- Session Storage Methods ---
  private CONTENT_MODE_KEY = 'mentra_content_mode';
  private PARENTAL_PIN_KEY = 'mentra_parental_pin';

  // Content mode methods
  async saveContentMode(mode: 'FULL'): Promise<void> {
    try {
      sessionStorage.setItem(this.CONTENT_MODE_KEY, mode);
    } catch (error) {
      console.error('Error saving content mode:', error);
      throw error;
    }
  }

  async getContentMode(): Promise<'FULL'> {
    try {
      const mode = sessionStorage.getItem(this.CONTENT_MODE_KEY) as 'FULL';
      return mode || 'FULL';
    } catch (error) {
      console.error('Error getting content mode:', error);
      return 'FULL';
    }
  }

  // PIN management
  async setParentalPin(pin: string): Promise<void> {
    try {
      sessionStorage.setItem(this.PARENTAL_PIN_KEY, pin);
    } catch (error) {
      console.error('Error setting parental PIN:', error);
      throw error;
    }
  }

  async getParentalPin(): Promise<string | null> {
    try {
      return sessionStorage.getItem(this.PARENTAL_PIN_KEY);
    } catch (error) {
      console.error('Error getting parental PIN:', error);
      return null;
    }
  }

  async clearParentalState(): Promise<void> {
    try {
      sessionStorage.removeItem(this.PARENTAL_PIN_KEY);
      sessionStorage.removeItem(this.CONTENT_MODE_KEY);
    } catch (error) {
      console.error('Error clearing parental state:', error);
      throw error;
    }
  }
}

export const localDbService = new LocalDatabaseService(); 